/**
 * What the inbox scan is doing for the signed-in user's firm: not connected,
 * scanning, found claims, none yet, or failed. The contract is InboxScanState
 * in shared/firstClaim.ts.
 *
 * TODO(email-to-first-claim pipeline): provide
 *
 *   GET /api/inbox-scan/status -> InboxScanState
 *
 * (isAuthenticated, scoped to the session's firm; claimIds newest first;
 * scannedCount/totalCount while scanning). This hook calls it first and needs
 * no change once the route answers with 200.
 *
 * Until then (404) the hook derives the only two states it can know honestly
 * from the firm's Gmail accounts: not_connected and none_yet. It never reports
 * scanning or found without the pipeline, so nothing here pretends a scan ran.
 */
import { useQuery } from '@tanstack/react-query';
import { INBOX_SCAN_STATUS_API, inboxScanStateSchema, type InboxScanState } from '@shared/firstClaim';
import { apiRequest } from '@/lib/query-client';

export const INBOX_SCAN_QUERY_KEY = [INBOX_SCAN_STATUS_API] as const;
/** How often to ask again while a scan is running. */
export const INBOX_SCAN_POLL_MS = 4000;

interface GmailAccountSummary {
  email?: string | null;
  isActive: boolean;
}

async function deriveFromGmailAccounts(): Promise<InboxScanState> {
  const accounts: GmailAccountSummary[] = await (await apiRequest('GET', '/api/gmail/accounts')).json();
  const active = accounts.find((account) => account.isActive);
  return {
    status: active ? 'none_yet' : 'not_connected',
    accountEmail: active?.email ?? null,
    claimIds: [],
    foundCount: 0,
  };
}

/** A failed status request, with its HTTP status so the retry rule can tell "not allowed" from "try again". */
export class InboxScanStatusError extends Error {
  constructor(public readonly status: number) {
    super(`Inbox scan status failed (${status})`);
    this.name = 'InboxScanStatusError';
  }
}

/**
 * Never retry 401/403: the route needs the claims-read permission (claims.read_all or claims.read_assigned), and asking
 * again will not change the answer. Other failures get one retry.
 */
export function shouldRetryInboxScan(failureCount: number, error: unknown): boolean {
  if (error instanceof InboxScanStatusError && (error.status === 401 || error.status === 403)) return false;
  return failureCount < 1;
}

export async function fetchInboxScanState(): Promise<InboxScanState> {
  const res = await fetch(INBOX_SCAN_STATUS_API, { credentials: 'include' });
  if (res.status === 404) return deriveFromGmailAccounts();
  if (!res.ok) throw new InboxScanStatusError(res.status);
  return inboxScanStateSchema.parse(await res.json());
}

export interface InboxScan {
  state: InboxScanState | null;
  isLoading: boolean;
}

export function useInboxScan(options: { enabled?: boolean } = {}): InboxScan {
  const query = useQuery<InboxScanState>({
    queryKey: INBOX_SCAN_QUERY_KEY,
    queryFn: fetchInboxScanState,
    enabled: options.enabled ?? true,
    staleTime: 0,
    retry: shouldRetryInboxScan,
    refetchInterval: (q) => (q.state.data?.status === 'scanning' ? INBOX_SCAN_POLL_MS : false),
  });

  if (query.isError) {
    return { state: { status: 'error', claimIds: [], foundCount: 0 }, isLoading: false };
  }
  return { state: query.data ?? null, isLoading: query.isLoading };
}
